import React from 'react';
import { createRoot } from 'react-dom/client';
import { FloatingWidget } from '../components/floating-widget/floating-widget';
import { InlineWidget } from '../components/inline-widget/inline-widget';
import { WidgetProvider } from '../context/widget-context';
import { fetchAssistantDetails } from '../lib/call-api';
import type { QCallFloatingConfig, QCallInlineConfig } from '../types/widget';
import '../styles/index.css';

export interface WidgetConfig {
  type?: 'floating' | 'inline';
  target?: string;
  position?: 'bottom-right' | 'bottom-left';
  uiStyle?: 'default' | 'minimal' | 'expanded';
  agentName?: string;
  avatar?: string;
  wsUrl?: string;
  theme?: 'light' | 'dark';
  primaryColor?: string;
  autoOpen?: boolean;
  pulse?: boolean;
  apiEndpoint?: string;
  assistantId?: string;
  apiKey?: string;
  userId?: string;
  workspaceId?: string;
  apiBaseUrl?: string;
  apiToken?: string;
  language?: string;
  voiceModal?: string;
  voiceName?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
}

declare global {
  interface Window {
    QCallWidget?: {
      init: (config: WidgetConfig) => void;
      destroy: () => void;
    };
    qcallWidgetConfig?: WidgetConfig;
  }
}

let root: ReturnType<typeof createRoot> | null = null;
let container: HTMLDivElement | null = null;

function applyThemeConfig(config: WidgetConfig) {
  if (config.primaryColor) {
    document.documentElement.style.setProperty('--qcall-primary', config.primaryColor);
    document.documentElement.style.setProperty('--qcall-primary-light', `${config.primaryColor}20`);
  }
  if (config.theme === 'dark') {
    document.documentElement.classList.add('qcall-dark');
  }
}

async function initWidget(config: WidgetConfig) {
  // Clean up existing
  if (root && container) {
    root.unmount();
    container.remove();
    root = null;
    container = null;
  }

  // Fetch assistant details from API
  let assistantDetails = null;
  if (config.assistantId && config.apiKey) {
    try {
      assistantDetails = await fetchAssistantDetails(
        config.assistantId,
        config.apiKey,
        config.apiBaseUrl
      );
    } catch (err) {
      console.error('[QCallWidget] Failed to fetch assistant details:', err);
      config.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  // Do not render widget if assistant not found or assistant_id is invalid
  if (config.assistantId && config.apiKey && !assistantDetails) {
    console.log('[QCallWidget] Assistant not found or invalid assistant_id, skipping render.');
    return;
  }

  // Check if widget is disabled via API config
  const apiWidgetConfig = assistantDetails?.widget_config;
  if (apiWidgetConfig && apiWidgetConfig.enabled === false) {
    console.log('[QCallWidget] Widget is disabled via widget_config.enabled, skipping render.');
    return;
  }
  const resolvedUiStyle = config.uiStyle ?? apiWidgetConfig?.uiStyle;
  const merged: WidgetConfig = {
    ...config,
    agentName: config.agentName || assistantDetails?.name || assistantDetails?.agent_name,
    avatar: config.avatar || assistantDetails?.assistant_image || assistantDetails?.avatar,
    uiStyle: resolvedUiStyle,
    position: config.position ?? apiWidgetConfig?.position,
    type: config.type || apiWidgetConfig?.mode || 'floating',
    target: config.target || apiWidgetConfig?.containerId,
  };

  applyThemeConfig(merged);

  // Create container
  if (merged.target) {
    const el = document.querySelector(merged.target);
    if (el) {
      container = document.createElement('div');
      container.id = 'qcall-widget-root';
      el.appendChild(container);
    } else {
      container = document.createElement('div');
      container.id = 'qcall-widget-root';
      document.body.appendChild(container);
    }
  } else {
    container = document.createElement('div');
    container.id = 'qcall-widget-root';
    document.body.appendChild(container);
  }

  container.setAttribute('data-qcall-initialized', 'true');
  root = createRoot(container);

  // Shared base config for WidgetProvider (matches QCallBaseConfig)
  const baseConfig = {
    wsUrl: merged.wsUrl,
    apiEndpoint: merged.apiEndpoint,
    assistantId: merged.assistantId,
    apiKey: merged.apiKey,
    userId: merged.userId,
    workspaceId: merged.workspaceId,
    apiBaseUrl: merged.apiBaseUrl,
    apiToken: merged.apiToken,
    language: merged.language,
    voiceModal: merged.voiceModal,
    voiceName: merged.voiceName,
    agentName: merged.agentName,
    avatar: merged.avatar,
    onConnect: merged.onConnect,
    onDisconnect: merged.onDisconnect,
    onError: merged.onError,
  };

  root.render(
    <React.StrictMode>
      <WidgetProvider config={baseConfig}>
        {merged.type === 'inline' ? (
          <InlineWidget
            config={{
              ...baseConfig,
              containerId: merged.target,
              showUseCaseCard: true,
            } as QCallInlineConfig}
          />
        ) : (
          <FloatingWidget
            config={{
              ...baseConfig,
              uiStyle: merged.uiStyle,
              position: merged.position,
              pulse: merged.pulse,
              autoOpen: merged.autoOpen,
            } as QCallFloatingConfig}
          />
        )}
      </WidgetProvider>
    </React.StrictMode>
  );
}

function destroyWidget() {
  if (root && container) {
    root.unmount();
    container.remove();
    root = null;
    container = null;
  }
}

// Auto-init from data attributes or global config
if (typeof window !== 'undefined') {
  const autoInit = () => {
    // 1. Global config
    if (window.qcallWidgetConfig) {
      initWidget(window.qcallWidgetConfig);
      return;
    }

    // 2. Data attributes on script tag
    const script = document.currentScript || Array.from(document.getElementsByTagName('script')).find(
      s => s.hasAttribute('data-assistant-id')
    );

    if (script) {
      const config: WidgetConfig = {
        assistantId: script.getAttribute('data-assistant-id') || undefined,
        apiKey: script.getAttribute('data-api-key') || undefined,
        apiBaseUrl: script.getAttribute('data-api-base-url') || undefined,
        wsUrl: script.getAttribute('data-ws-url') || undefined,
        userId: script.getAttribute('data-user-id') || undefined,
        workspaceId: script.getAttribute('data-workspace-id') || undefined,
        language: script.getAttribute('data-language') || undefined,
        agentName: script.getAttribute('data-agent-name') || undefined,
        avatar: script.getAttribute('data-avatar') || undefined,
        type: (script.getAttribute('data-type') as WidgetConfig['type']) || undefined,
        target: script.getAttribute('data-target') || undefined,
        uiStyle: (script.getAttribute('data-ui-style') as WidgetConfig['uiStyle']) || undefined,
        position: (script.getAttribute('data-position') as WidgetConfig['position']) || undefined,
        theme: (script.getAttribute('data-theme') as WidgetConfig['theme']) || undefined,
        primaryColor: script.getAttribute('data-primary-color') || undefined,
        autoOpen: script.getAttribute('data-auto-open') === 'true',
        pulse: script.getAttribute('data-pulse') !== 'false',
      };

      if (config.assistantId) {
        initWidget(config);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}

export { initWidget as init, destroyWidget as destroy };
